import i18n from 'modules/core/lib/i18n'
import type { TicketsConnectionQuery } from 'generated/resources/graphql'
import { formatTimeRemaining, isOverdue } from 'modules/ticketing/lib/ticketTime'
import { formatTicketId } from 'modules/ticketing/lib/ticketFormat'

export type TicketNode = TicketsConnectionQuery['ticketing']['ticketsConnection']['nodes'][number]
import {
  getTicketStatusLabel,
  getTicketStatusTableVariant,
} from 'modules/ticketing/lib/ticketStatus'
import {
  getTicketPriorityLabel,
  getTicketPriorityVariant,
} from 'modules/ticketing/lib/ticketPriority'

const ONE_HOUR = 3600
const FOUR_HOURS = 14400
const ONE_DAY = 86400

export const ALLOWED_FILTERS = [
  'status',
  'priority',
  'sla_remaining',
  'assignee_team',
  'assignee_member',
  'requester',
] as const

export const ALLOWED_COLUMNS = [
  'id',
  'title',
  'assignee_team',
  'assignee_member',
  'status',
  'priority',
  'time_remaining',
] as const

const SLA_TYPES = {
  OVERDUE: {
    label: () => i18n.t('ticketing.inbox.sla.overdue'),
    value: 'overdue',
    params: { slaRemainingOverdue: true },
  },
  UNDER_1H: {
    label: () => i18n.t('ticketing.inbox.sla.under_1h'),
    value: 'under_1h',
    params: { slaRemainingMaxSeconds: ONE_HOUR },
  },
  OVER_1H: {
    label: () => i18n.t('ticketing.inbox.sla.1h_4h'),
    value: '1h_4h',
    params: { slaRemainingMinSeconds: ONE_HOUR, slaRemainingMaxSeconds: FOUR_HOURS },
  },
  OVER_4H: {
    label: () => i18n.t('ticketing.inbox.sla.4h_24h'),
    value: '4h_24h',
    params: { slaRemainingMinSeconds: FOUR_HOURS, slaRemainingMaxSeconds: ONE_DAY },
  },
  OVER_24H: {
    label: () => i18n.t('ticketing.inbox.sla.over_24h'),
    value: 'over_24h',
    params: { slaRemainingMinSeconds: ONE_DAY },
  },
} as const

export function getSlaOptions() {
  return Object.values(SLA_TYPES).map((type) => ({
    label: type.label(),
    value: type.value,
  }))
}

export function getSlaParamsForValue(slaValue: string) {
  return Object.values(SLA_TYPES).find((type) => type.value === slaValue)?.params
}

type ColumnDefinition = {
  key: string
  label: string
  sorting?: 'status' | 'priority' | 'slaDueAt' | 'timeRemaining'
  render: (
    item: TicketNode
  ) =>
    | { type: 'text'; value: string }
    | { type: 'status'; value: { status: string; label: string } }
}

export function getColumnDefinitions(): ColumnDefinition[] {
  return [
    {
      key: 'id',
      label: i18n.t('ticketing.inbox.columns.id'),
      render: (item) => ({ type: 'text', value: formatTicketId(item.id) }),
    },
    {
      key: 'title',
      label: i18n.t('ticketing.inbox.columns.title'),
      render: (item) => ({ type: 'text', value: item.title }),
    },
    {
      key: 'assignee_team',
      label: i18n.t('ticketing.inbox.columns.assignee_team'),
      render: (item) => ({
        type: 'text',
        value: item.assigneeTeamName ?? '-',
      }),
    },
    {
      key: 'assignee_member',
      label: i18n.t('ticketing.inbox.columns.assignee_member'),
      render: (item) => ({
        type: 'text',
        value: item.assigneeMemberName ?? '-',
      }),
    },
    {
      key: 'status',
      label: i18n.t('ticketing.inbox.columns.status'),
      sorting: 'status',
      render: (item) => ({
        type: 'status',
        value: {
          status: getTicketStatusTableVariant(item.status),
          label: getTicketStatusLabel(item.status),
        },
      }),
    },
    {
      key: 'priority',
      label: i18n.t('ticketing.inbox.columns.priority'),
      sorting: 'priority',
      render: (item) => ({
        type: 'status',
        value: {
          status: getTicketPriorityVariant(item.priority),
          label: getTicketPriorityLabel(item.priority),
        },
      }),
    },
    {
      key: 'time_remaining',
      label: i18n.t('ticketing.inbox.columns.time_remaining'),
      sorting: 'timeRemaining',
      render: (item) => {
        if (isOverdue(item.timeRemainingToSlaSeconds)) {
          return {
            type: 'status',
            value: {
              status: 'critical',
              label: formatTimeRemaining(item.timeRemainingToSlaSeconds),
            },
          }
        }
        return {
          type: 'text',
          value: formatTimeRemaining(item.timeRemainingToSlaSeconds),
        }
      },
    },
  ]
}

export function buildTableColumns() {
  const allowedColumns = new Set<string>(ALLOWED_COLUMNS)
  return getColumnDefinitions()
    .filter((col) => allowedColumns.has(col.key))
    .map(({ label, render, sorting }) => ({ label, render, sorting }))
}
